import { Button, Image } from '@outdoor-voices/ui';
import cc from 'classcat';
import cookie from 'js-cookie';
import React, { useRef, useState } from 'react';
import useKeyFocus from '@outdoor-voices/ui/dist/utils/hooks/useKeyFocus';

import { RedemptionOption } from './types';
import { COOKIE_DOMAIN, IS_PRODUCTION, SHOPIFY_URL } from '../../config/constants';
import { useEffect } from 'react';
import { useLoyaltyRedemptionOptionRedeemMutation } from '../../generated/graphql';
import css from './card.module.scss';

type Props = {
  minimal: boolean;
  option: RedemptionOption;
};
const Card: React.FC<Props> = ({ minimal, option }) => {
  const rootRef = useRef(null);
  const [mutate, { data, loading }] = useLoyaltyRedemptionOptionRedeemMutation();
  const { amount, description, icon, discountAmountCents, redeemable } = option;
  const { open, setOpen } = useKeyFocus(false, rootRef);
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  const onBeginRedeem = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    setOpen(true);
  };

  const onCancelRedeem = () => {
    setOpen(false);
  };

  const onConfirm = async () => {
    const { data: mutateData } = await mutate({ variables: { id: option.id, name: option.name } });

    if (mutateData.loyaltyRedemptionOptionRedeem.ok) {
      const opts = { expires: 1, domain: COOKIE_DOMAIN, secure: IS_PRODUCTION };
      const { rewardText } = mutateData.loyaltyRedemptionOptionRedeem;
      cookie.set('ov-promo-code', JSON.stringify({ promoCode: rewardText }), opts);

      setTimeout(() => {
        setConfirmed(true);
      }, 50);
    }
  };

  // const onConfirmClose = () => {
  //   setOpen(false);
  // };

  const copyToClipboard = async () => {
    await navigator.clipboard?.writeText(data?.loyaltyRedemptionOptionRedeem?.rewardText);
    setCopied(true);
  };

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timeout = setTimeout(() => {
      setCopied(false);
    }, 2000);

    return () => {
      clearInterval(timeout);
    };
  }, [copied]);

  const price = <>${discountAmountCents / 100}</>;
  const tabIndex = open ? 0 : -1;

  return (
    <>
      <div className={css.root}>
        <div className={cc([css.content, { [css.on]: open || confirmed }])} ref={rootRef}>
          <div className={css.image}>
            <Image alt={description} height={1200} width={900} src={icon} sizes={[300, 400]} />
          </div>
          {minimal && redeemable && (
            <div className={css.back}>
              {confirmed && data.loyaltyRedemptionOptionRedeem.ok && (
                <>
                  {/* <div className={css.close}>
                    <Button ariaLabel="Click to close" linkStyle onClick={onConfirmClose} tabIndex={tabIndex}>
                      <XLarge />
                    </Button>
                  </div> */}
                  <p className={css.title}>Nice work!</p>
                  <p className={css.copy}>Copy code to apply at online checkout.</p>
                  <p className={css.copy}>
                    At a shop? <br />
                    Show the code in-store.
                  </p>
                  <p className={css.copy}>
                    If you&rsquo;d prefer to use your reward later, your code will be in your inbox.
                  </p>
                  <div className={css.actions}>
                    <Button ariaLabel="Confirm the redemption" onClick={copyToClipboard} tabIndex={tabIndex}>
                      {data?.loyaltyRedemptionOptionRedeem?.rewardText}
                      <div className={cc([css.copied, { [css.on]: copied }])}>Copied!</div>
                    </Button>
                    <Button ariaLabel="Cancel the redemption" href={`${SHOPIFY_URL}cart`} linkStyle tabIndex={tabIndex}>
                      Head to Cart
                    </Button>
                  </div>
                </>
              )}
              {!confirmed && (
                <>
                  <p className={css.title}>Ready to Redeem?</p>
                  <p className={css.copy}>
                    Confirming will convert your points to a reward. This can&rsquo;t be reversed.
                    <br />
                    <a href={`${SHOPIFY_URL}pages/terms-of-use`} target="_blank" rel="noreferrer">
                      See terms &amp; conditions
                    </a>
                  </p>
                  {data && !data.loyaltyRedemptionOptionRedeem.ok && (
                    <p className={css.copy}>
                      <strong>
                        Sorry, we weren&rsquo;t able to redeem this reward. Please try again or get in touch.
                      </strong>
                    </p>
                  )}
                  <div className={css.actions}>
                    <Button
                      ariaLabel="Confirm the redemption"
                      disabled={loading}
                      onClick={onConfirm}
                      tabIndex={tabIndex}
                    >
                      {loading ? 'Redeeming...' : 'Confirm'}
                    </Button>
                    <Button
                      ariaLabel="Cancel the redemption"
                      disabled={loading}
                      linkStyle
                      onClick={onCancelRedeem}
                      tabIndex={tabIndex}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={css.discount}>{price} Off</div>
      <div className={css.amount}>{amount} Points</div>
      {minimal && redeemable && !confirmed && (
        <div className={css.button}>
          <Button ariaLabel="Click to confirm redemption" onClick={onBeginRedeem}>
            Redeem
          </Button>
        </div>
      )}
    </>
  );
};

export default Card;
